import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { type CrmObject, getPropertyValue } from '@/types/crm';

import type { CardFieldSlot, PipelineCardConfig } from './cardConfig';

export type ResolvedSlot =
  | { kind: 'property'; schema: PropertyTypeSchema; value: unknown }
  | { kind: 'association'; target: CrmObject }
  | null;

export function resolveSlot(
  slot: CardFieldSlot,
  object: CrmObject,
  propertySchemas: Record<string, PropertyTypeSchema>,
  associatedObjects: Record<string, CrmObject>
): ResolvedSlot {
  let direct: ResolvedSlot = null;

  if (slot.type === 'property') {
    const schema: PropertyTypeSchema | undefined = propertySchemas[slot.name];
    if (schema !== undefined) {
      const value = getPropertyValue<unknown>(object.properties?.[slot.name]);
      if (value !== null && value !== undefined && value !== '') {
        direct = { kind: 'property', schema, value };
      }
    }
  } else {
    const detail = object.associations?.find(
      a => a.association_type_name === slot.name
    );
    const target = detail
      ? associatedObjects[detail.target_entity_id]
      : undefined;
    if (target !== undefined) {
      direct = { kind: 'association', target };
    }
  }

  if (direct !== null) {
    return direct;
  }

  for (const fallback of slot.fallbacks ?? []) {
    const fallbackResult = resolveSlot(
      fallback,
      object,
      propertySchemas,
      associatedObjects
    );
    if (fallbackResult !== null) {
      return fallbackResult;
    }
  }

  return null;
}

export function validateCardConfig(
  config: PipelineCardConfig,
  propertySchemas: Record<string, PropertyTypeSchema>,
  associationTypeMap: Map<string, string>
): PipelineCardConfig {
  function isValidRef(slot: CardFieldSlot): boolean {
    return slot.type === 'property'
      ? Object.hasOwn(propertySchemas, slot.name)
      : associationTypeMap.has(slot.name);
  }

  // Flatten a slot's preference chain (primary first, then fallbacks
  // depth-first) into a single ordered list of leaf refs.
  function flattenChain(slot: CardFieldSlot, out: CardFieldSlot[]): void {
    const leaf: CardFieldSlot = { type: slot.type, name: slot.name };
    if (slot.showLabel) {
      leaf.showLabel = true;
    }
    out.push(leaf);
    for (const fallback of slot.fallbacks ?? []) {
      flattenChain(fallback, out);
    }
  }

  function filterSlot(slot: CardFieldSlot): CardFieldSlot | null {
    // Keep only refs that still exist in the schema, preserving order. The
    // first surviving ref becomes the primary so a schema/association change
    // that invalidates the primary rebinds to the next valid fallback instead
    // of dropping the whole user-configured chain.
    const chain: CardFieldSlot[] = [];
    flattenChain(slot, chain);
    const valid = chain.filter(isValidRef);

    if (valid.length === 0) {
      return null;
    }

    const [primary, ...fallbacks] = valid;
    return {
      ...primary,
      fallbacks: fallbacks.length > 0 ? fallbacks : undefined,
    };
  }

  const filteredRows = config.rows
    .map(row => ({
      ...row,
      slots: row.slots
        .map(filterSlot)
        .filter((slot): slot is CardFieldSlot => slot !== null),
    }))
    .filter(row => row.slots.length > 0);

  return { rows: filteredRows };
}

export function extractConfigNames(config: PipelineCardConfig): {
  propertyNames: string[];
  associationNames: string[];
} {
  const propertyNames = new Set<string>();
  const associationNames = new Set<string>();

  function walkSlot(slot: CardFieldSlot): void {
    if (slot.type === 'property') {
      propertyNames.add(slot.name);
    } else {
      associationNames.add(slot.name);
    }
    for (const fallback of slot.fallbacks ?? []) {
      walkSlot(fallback);
    }
  }

  for (const row of config.rows) {
    for (const slot of row.slots) {
      walkSlot(slot);
    }
  }

  return {
    propertyNames: [...propertyNames],
    associationNames: [...associationNames],
  };
}
